import React, { useState } from 'react'
import { Image, ImageStyle, TextStyle, TouchableOpacity, View } from 'react-native'
import { useNavigation } from '@react-navigation/core'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { StackNavigationProp } from '@react-navigation/stack'
import { useDispatch } from 'react-redux'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Analytics, CustomEventTypes, buildTutorialMixpanelEventProperties } from '@src/config'
import { Button, Text } from '@components/base'
import { InlineAlert, ProgressBar, ScrollViewWithFade, VideoPlayer } from '@components'
import { openUrl } from '@src/utils'
import { NavigationContainer } from '@src/screens/Common/containers'
import { AppStackParamList, OnboardingStackParamsList } from '@src/navigation/types'
import { useIsIndirectSyncSensorMode } from '@src/screens/OwnSensorOnboarding/hooks'
import { SensorModel, UiStateNames } from '@src/types'
import { Tutorial, TutorialSensorKind } from '../models/tutorials.types'
import {
  isDexcomG6G7RealtimeTutorial,
  isPlatformLink,
  isSteloRealtimeTutorial,
  isThirdPartyIntegrationTutorial,
  isTutorialLink,
  isTutorialVideo,
  platformLinkUrl,
  shouldNavigateToNextTutorial,
} from './utils'

const Link = ({ text, url, style }: { text: string; url: string; style: TextStyle }) => (
  <TouchableOpacity accessibilityLabel={text} onPress={() => openUrl(url)}>
    <Text type="regular" style={style}>
      {text}
    </Text>
  </TouchableOpacity>
)

export const CarouselTutorial = ({ tutorial }: { tutorial: Tutorial }) => {
  const styles = useStyleSheet(themedStyle)
  const insets = useSafeAreaInsets()
  const navigation = useNavigation<
    StackNavigationProp<AppStackParamList & OnboardingStackParamsList>
  >()
  const [currentStepIndex, setCurrentStepIndex] = useState(0)

  const { tutorialSteps } = tutorial

  const isIndirectSyncSensorMode = useIsIndirectSyncSensorMode()
  const dispatch = useDispatch()

  if (tutorialSteps.length === 0) {
    return null
  }

  const currentStep = tutorialSteps[currentStepIndex]
  const isLastStep = currentStepIndex === tutorialSteps.length - 1

  const videoAction = currentStep.action.find(isTutorialVideo)
  const linkAction = currentStep.action.find(isTutorialLink)
  const platformLink = currentStep.action.find(isPlatformLink)
  const buttonUrl = platformLink ? platformLinkUrl(platformLink) : linkAction?.url

  const onBackPress = () => {
    if (currentStepIndex === 0) {
      navigation.goBack()
    } else {
      setCurrentStepIndex(currentStepIndex - 1)
    }
  }

  const onNextPress = () => {
    setCurrentStepIndex(currentStepIndex + 1)
  }

  const byosTutorialCompletionSideEffects = () => {
    if (isIndirectSyncSensorMode) {
      dispatch({
        type: 'app/upsertUiState',
        payload: {
          name: UiStateNames.OwnSensorTutorialCompleted, // we mark onboarding completion in 'check connection'
          value: true,
        },
      })
    }
  }

  const postTutorialNavigation = () => {
    // links from the CMS take precedence
    if (platformLink && shouldNavigateToNextTutorial(platformLink)) {
      openUrl(platformLinkUrl(platformLink))
      return
    }

    if (buttonUrl) {
      navigation.reset({
        index: 0,
        routes: [{ name: 'Drawer', state: { index: 0, routes: [{ name: 'Dashboard' }] } }],
      })
      openUrl(buttonUrl)
      return
    }

    if (isSteloRealtimeTutorial(tutorial)) {
      return navigation.navigate('DexcomSensorSettings', {
        autoToggleOn: true,
        sensorModel: SensorModel.DexcomStelo,
      })
    }

    if (isDexcomG6G7RealtimeTutorial(tutorial)) {
      return navigation.navigate('DexcomSensorSettings', {
        autoToggleOn: true,
        sensorModel:
          tutorial.sensorKind === TutorialSensorKind.DexcomG6Realtime
            ? SensorModel.DexcomG6
            : SensorModel.DexcomG7,
      })
    }

    // connections via Apple Health, Health Connect, or Terra
    if (isThirdPartyIntegrationTutorial(tutorial)) {
      return navigation.navigate('CheckConnection')
    }

    navigation.replace('Drawer', { screen: 'Dashboard' })
  }

  const handleLastStepPress = () => {
    Analytics.track(
      CustomEventTypes.TutorialCompleted,
      buildTutorialMixpanelEventProperties({
        tutorialId: tutorial.id,
        tutorialName: tutorial.name,
        sensorKind: tutorial.sensorKind,
        platform: tutorial.platform ?? undefined,
      }),
      `${CustomEventTypes.TutorialCompleted}/${tutorial.id}`,
    )
    byosTutorialCompletionSideEffects()
    postTutorialNavigation()
  }

  return (
    <NavigationContainer title={currentStep.title}>
      <View style={[styles.container, { paddingBottom: insets.bottom + 16 }]}>
        <ProgressBar
          style={styles.progressBar}
          currentStep={currentStepIndex}
          stepsCount={tutorialSteps.length}
          duration={900}
        />
        <ScrollViewWithFade scrollViewStyle={styles.scrollView}>
          {currentStep.image?.data && !videoAction && (
            <Image
              source={{ uri: currentStep.image.data.attributes.url }}
              style={styles.media as ImageStyle}
            />
          )}
          {videoAction?.video?.data && (
            <VideoPlayer
              key={videoAction.video.data.attributes.url}
              source={{ uri: videoAction.video.data.attributes.url }}
              style={styles.media}
              showControls={false}
              repeat
              muted
              playOnLoad
            />
          )}
          {currentStep.description && (
            <Text type="regular" style={styles.description}>
              {currentStep.description}
            </Text>
          )}
          {currentStep.warning && (
            <InlineAlert style={styles.warningMessage} message={currentStep.warning} />
          )}
          {linkAction && !isLastStep && !platformLink && (
            <Link text={currentStep.buttonText} url={linkAction.url} style={styles.linkAction} />
          )}

          {platformLink && !isLastStep && (
            <Link
              text={currentStep.buttonText}
              url={platformLinkUrl(platformLink)}
              style={styles.linkAction}
            />
          )}
        </ScrollViewWithFade>
        <View style={styles.buttonsContainer}>
          <Button
            type="outline"
            size="block"
            style={styles.backButton}
            onPress={onBackPress}
            accessibilityLabel="Back"
          >
            Back
          </Button>
          {!isLastStep ? (
            <Button
              type="primary"
              size="block"
              style={styles.nextButton}
              onPress={onNextPress}
              accessibilityLabel="Next"
            >
              Next
            </Button>
          ) : (
            <Button
              type="primary"
              size="block"
              style={styles.nextButton}
              onPress={handleLastStepPress}
              accessibilityLabel={currentStep.buttonText || 'Next'}
            >
              {currentStep.buttonText}
            </Button>
          )}
        </View>
      </View>
    </NavigationContainer>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    backgroundColor: 'theme.background',
  },
  progressBar: {
    margin: 16,
  },
  scrollView: {
    padding: 16,
  },
  media: {
    width: '100%',
    maxWidth: 600,
    maxHeight: 600,
    alignSelf: 'center',
    height: undefined,
    aspectRatio: 1,
    borderRadius: 16,
  },
  description: {
    marginTop: 24,
    marginBottom: 16,
  },
  warningMessage: {
    marginVertical: 16,
  },
  linkAction: {
    marginBottom: 16,
    color: 'theme.text.link',
    textDecorationLine: 'underline',
  },
  buttonsContainer: {
    flexDirection: 'row',
    paddingTop: 16,
    paddingHorizontal: 16,
  },
  backButton: {
    flex: 1,
    marginRight: 8,
  },
  nextButton: {
    flex: 2,
  },
})
